import React from "react";
import LegalDocument from "@/components/site/LegalDocument";

const content = String.raw`
**Kings Wake Digital Private Limited** ("Company", "we", "us", "our") respects your privacy. This Privacy Policy explains what information we collect when you use the mobile application **Jantaquid** (the "App"), how we use it, who we share it with, and what rights you have.

This policy is issued in accordance with the **Digital Personal Data Protection Act, 2023 ("DPDP Act")**, the **Information Technology Act, 2000**, and the rules made under them.

For the purposes of the DPDP Act, we act as the **Data Fiduciary** and you are the **Data Principal**.

**Registered address:** [FULL REGISTERED OFFICE ADDRESS]
**Website:** https://kingswake.co.in
**Email:** Team@kingswake.co.in

---

## 1. What We Collect

We have deliberately designed Jantaquid to collect as little personal data as possible. The App does **not** ask for your name, email address, phone number, photographs, contacts, location, or access to your camera or microphone.

### 1.1 Device and technical information

Collected automatically when you use the App:

- Device model, manufacturer and operating system version
- App version and build
- Device language and time zone
- **Advertising identifier** (Google Advertising ID)
- IP address
- Approximate country/region derived from IP address
- Crash reports and diagnostic data

### 1.2 Gameplay and usage information

- Levels started and completed, moves used, time taken
- Tube style and sound preferences
- Progress and unlocked levels
- In-app screens visited and features used
- Advertisements shown, viewed or completed

### 1.3 Rewards linking information

If you choose to link a rewards account, we collect:

- The one-time linking code you enter
- A randomly generated installation identifier
- A link identifier connecting this installation to your rewards account
- Records of gameplay events used to calculate rewards

Linking is **entirely optional**. The App works fully without it. We do **not** receive your name, email, phone number or password from the rewards service through this linking process.

### 1.4 Information stored only on your device

Some information never leaves your phone unless you link a rewards account — for example, your level progress, chosen tube style, sound setting and acceptance of our Terms. This is stored locally on your device and is deleted when you uninstall the App.

A cryptographic signing key used to secure reward communication is stored in the **Android Keystore** on your device and is never transmitted to us or to any third party.

---

## 2. Why We Use Your Information

| Purpose | What we use |
|---|---|
| Run the game and save your progress | Device info, gameplay data |
| Show advertisements | Advertising identifier, IP address, ad interaction data |
| Calculate and credit rewards | Linking data, gameplay events |
| Prevent fraud and abuse | Device info, installation identifier, gameplay timing |
| Fix crashes and improve the App | Crash reports, diagnostic and usage data |
| Meet legal obligations | Any of the above, where legally required |

**Legal basis.** Under the DPDP Act we process your personal data on the basis of the **consent** you give when you accept these terms on first launch, and where processing is necessary for **legitimate uses** permitted by the Act, such as complying with law or preventing fraud.

---

## 3. Advertising

Jantaquid is free and supported by advertising. Advertisements are served by third-party advertising networks.

These networks may collect or receive your advertising identifier, IP address, device information and information about the advertisements you see, in order to select, deliver, measure and personalise advertising. They act as independent data controllers in respect of that information and handle it under their own privacy policies.

**Advertising partners currently in use:**

- [AD NETWORK 1] — [PRIVACY POLICY URL]
- [AD NETWORK 2] — [PRIVACY POLICY URL]

*(This list will be updated when advertising is enabled. Only networks actually integrated in the App will be listed.)*

### How to limit ad personalisation

**Android:** Settings → Google → Ads → **Delete advertising ID** or **Opt out of Ads Personalisation**

After opting out you will still see advertisements, but they will not be personalised based on your advertising identifier.

### Important

Do **not** click on advertisements only to obtain rewards. Artificial or fraudulent ad activity breaches our Terms of Service and the policies of our advertising partners, and may result in loss of rewards and suspension of your account.

---

## 4. Who We Share Information With

We do **not sell** your personal data.

We share information only in these situations:

- **Advertising networks** — as described in Section 3
- **Analytics and crash reporting providers** — to understand usage and fix defects
- **Cloud hosting and infrastructure providers** — who store data on our behalf under contractual safeguards
- **The rewards service you link to** — only the gameplay events needed to calculate your rewards, and only if you choose to link
- **Legal and safety** — where required by law, court order, or a valid request from a government authority, or to protect our rights, users or systems
- **Business transfer** — if the Company is merged, acquired or reorganised, in which case the recipient remains bound by this policy

Every processor we engage is bound by contract to handle your data only on our instructions and to maintain reasonable security safeguards.

---

## 5. Where Your Data Is Stored

Our servers are located in **India**. Some of our service providers, including advertising and analytics partners, may process data outside India. Where that happens, transfers are made in accordance with the DPDP Act and applicable restrictions notified by the Central Government.

---

## 6. How Long We Keep Your Data

- **Gameplay and reward records:** retained while your linked account is active, and for up to **24 months** after your last activity
- **Crash and diagnostic data:** up to **12 months**
- **Records we must keep by law:** for the period required under Indian law
- **Data on your device:** removed when you uninstall the App

When personal data is no longer needed for the purpose it was collected for, and no legal obligation requires us to keep it, we erase it.

---

## 7. Security

We use reasonable security safeguards to protect your data, including:

- Encrypted connections (HTTPS/TLS) for all communication
- Cryptographic request signing for reward events, with keys held in the Android Keystore
- Access controls limiting who within the Company can access personal data
- Rate limiting, deduplication and anti-abuse checks

No system is completely secure. If a personal data breach occurs, we will notify the Data Protection Board of India and affected users as required under the DPDP Act.

---

## 8. Your Rights

Under the DPDP Act, you have the right to:

- **Access** — obtain a summary of the personal data we process about you and who we have shared it with
- **Correction and erasure** — have inaccurate data corrected, completed or updated, and have data erased where it is no longer needed
- **Withdraw consent** — withdraw consent at any time, as easily as you gave it. Withdrawal does not affect processing already carried out
- **Grievance redressal** — raise a complaint with us using the details in Section 11
- **Nominate** — nominate another individual to exercise your rights in the event of your death or incapacity
- **Complain to the Board** — approach the **Data Protection Board of India** if your grievance is not satisfactorily resolved by us

To exercise any of these rights, write to **Team@kingswake.co.in**. We will respond as promptly as possible and in any case within the timelines prescribed under applicable law. We may need to verify your identity, or ask for the installation identifier shown in the App, before acting on a request.

**Note:** Withdrawing consent or requesting erasure of your linking data will end reward accrual and may remove your ability to earn rewards through the App.

---

## 9. Children

Jantaquid is **not intended for children below the age of 18 years**.

Under the DPDP Act, we may process the personal data of a child only with **verifiable consent of a parent or lawful guardian**, and we must not undertake tracking, behavioural monitoring, or targeted advertising directed at children.

Accordingly:

- We do not knowingly collect personal data from anyone under 18 without verifiable parental consent
- We do not knowingly direct advertising at children
- If we become aware that we have collected personal data from a child without such consent, we will delete it promptly

If you are a parent or guardian and believe your child has provided us with personal data, contact **Team@kingswake.co.in** and we will act on it.

---

## 10. Third-Party Links

The App may display advertisements or links that take you to websites and services operated by others. We do not control those services and are not responsible for their content or privacy practices. Please read their privacy policies before providing them with any information.

---

## 11. Grievance Officer

In accordance with the Information Technology Act, 2000, the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, and the DPDP Act, 2023, you may contact:

**Grievance Officer / Data Protection Contact**
Kings Wake Digital Private Limited
Email: **Team@kingswake.co.in**
Website: https://kingswake.co.in
Address: [FULL REGISTERED OFFICE ADDRESS]

We will acknowledge your complaint within **24 hours** and endeavour to resolve it within **15 days** of receipt.

If you are not satisfied with our response, you may escalate the matter to the **Data Protection Board of India**.

---

## 12. Changes to This Policy

We may update this Privacy Policy from time to time. When we do, we will revise the "Last Updated" date at the top and, where the change is material, notify you within the App or on our website.

Your continued use of the App after a revised policy takes effect means you accept the updated policy.

---

## 13. Contact Us

**Kings Wake Digital Private Limited**
Email: **Team@kingswake.co.in**
Website: **https://kingswake.co.in**

---

*Placeholders to complete before publishing: full registered office address, CIN, and the final list of advertising partners once ad networks are integrated.*
`;

export default function JantaquidPrivacyPage() {
  return <LegalDocument title="Jantaquid Privacy Policy" description="Privacy policy for the Jantaquid mobile game from Kings Wake Digital Private Limited." content={content} alternate={{ href: "/jantaquid/terms-and-conditions", label: "Read the Terms & Conditions" }} />;
}
